import _ from 'lodash';

import ConfigStorage from '../../src/configs';
import {
  ActionSignOut,
  ActionSucceedAuth,
} from '../../src/store/actions/actions-user';
import {NotificationError} from '../../src/services/notifications';

let store;
let rSession;

const {API_HOST} = ConfigStorage().API_SETTINGS;

const supportedApiLangs = ['ru', 'en'];

function trimLeft(str, replacement) {
  while (str.charAt(0) === replacement) {
    str = str.substr(1);
  }
  return str;
}

/**
 * Prepend host of API server
 * @param hostname
 * @param path
 * @returns {String}
 * @private
 */
function createURL(path) {
  return `${API_HOST}/${trimLeft(path, '/')}`;
}

export function registerStore(_store) {
  store = _store;

  // init state
  const {rSession: initRSession} = store.getState();
  rSession = initRSession;

  // subscribe for update
  store.subscribe(() => {
    const {rSession: nextRSession} = store.getState();
    if (!_.isEqual(nextRSession, rSession)) {
      rSession = nextRSession;
    }
  });
}

/**
 * This is our overly complicated isomorphic "request"
 * @param hostname
 * @param basicAuthorization
 * @param login
 * @param accessToken
 * @returns {Function}
 */

const getTokenData = () => {
  const loggedIn = rSession && rSession.LOGGED_IN;
  return loggedIn && rSession.tokenData;
};

const getAccessToken = () => {
  const token = getTokenData();
  return token && token.access_token;
};

const getRefreshToken = () => {
  const token = getTokenData();
  return token && token.refresh_token;
};

const saveStoreUser = (data, skipServerLogout = false) => {
  if (data) {
    return store.dispatch(ActionSucceedAuth(data));
  }
  return store.dispatch(ActionSignOut(skipServerLogout));
};

const showModalErrorRefreshToken = (error) => {
  NotificationError(error.error_description || error.message);
};

export function setupRequest({basicAuthorization, clientAuthorization, login = '/login'}) {
  if (!store) {
    throw Error('core/helpers/request: should be registered redux store before make a request');
  }

  let isTokenUpdating = false;

  /**
   * Parse response
   * @param resp
   * @returns {Promise}
   * @private
   */
  function handleResponse(resp) {
    const redirect = resp.headers.get('Location');
    if (redirect) {
      if (process.env.BROWSER) {
        window.location.replace(redirect);
      }
      return Promise.resolve({redirect});
    }

    const contentType = resp.headers && resp.headers.get('Content-Type');
    const isJSON = contentType && contentType.includes('json');
    const response = resp[isJSON ? 'json' : 'text']();

    return resp.ok
      ? response
      : response.then((err) => {
        if (resp.status === 401) {
          if (process.env.BROWSER) {
            const urlToReplace = `${login}?prev=${window.location.pathname}`;
            window.location.replace(urlToReplace);
          }
          return Promise.resolve({redirect: login});
        }
        if (process.env.DEV) {
          console.error('requestError:', err);
        }
        throw err;
      });
  }

  /**
   * Make request
   * @param url
   * @param options
   * @returns {Promise}
   * @private
   */
  function request(url, options = {}) {
    const requestOptions = options;
    const bearerToken = getAccessToken();
    const requestURL = createURL(url);

    if (!requestOptions.headers) {
      requestOptions.headers = {};
    }

    const {headers, body = {}, auth, method} = requestOptions;

    const {lang} = document.documentElement;
    if (supportedApiLangs.indexOf(lang) !== -1) {
      headers['Accept-Language'] = lang;
    }

    // Append auth
    const supportableAuths = ['Bearer', 'Basic', 'Client', 'none'];

    if (supportableAuths.indexOf(auth) === -1) {
      if (process.env.DEV) {
        console.info('auth method not found:', auth);
      }
    }

    if (auth === 'Bearer' && bearerToken) {
      headers.Authorization = `Bearer ${bearerToken}`;
    }
    if (!auth || auth === 'Basic' || (auth === 'Bearer' && !bearerToken)) {
      headers.Authorization = basicAuthorization;
    }
    if (auth === 'Client') {
      if (_.isObject(body)) Object.assign(body, clientAuthorization);
    }

    if (body && !(body instanceof FormData)) {
      if (method !== 'GET') requestOptions.body = _.isObject(body) ? JSON.stringify(body) : body;
      if (method === 'GET' && !_.isEmpty(body)) requestOptions.body = JSON.stringify(body);
      Object.assign(headers, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      });
    }

    if (process.env.DEV) {
      //console.info('requestURL:', requestURL);
    }

    return fetch(requestURL, requestOptions);
  }

  /**
   * Update refresh token if access token is expired
   * @returns {Promise}
   * @private
   */
  async function updateRefreshToken(options) {
    const token = getRefreshToken();
    if (!token) {
      isTokenUpdating = false;
      await saveStoreUser(null, true);
      return Promise.resolve({redirect: login});
    }
    const body = {
      grant_type: 'refresh_token',
      refresh_token: token,
    };
    const requestOptions = {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: basicAuthorization,
      },
    };
    const requestURL = createURL('/oauth');

    if (process.env.DEV) {
      //console.info('requestURL:', requestURL);
    }

    return fetch(requestURL, requestOptions)
      .then((data) => data.json())
      .then(async (result) => {
        if (result.access_token) {
          await saveStoreUser(result);
          isTokenUpdating = false;
          return request(options.url, options.requestOptions).then(handleResponse);
        } if (result.error) {
          isTokenUpdating = false;
          await saveStoreUser(null, false);
          showModalErrorRefreshToken(result);
          return null;
        }
        await saveStoreUser(null, true);
        isTokenUpdating = false;
        if (process.env.BROWSER) {
          const urlToReplace = `${login}?prev=${window.location.pathname}`;
          window.location.replace(urlToReplace);
        }
        return Promise.resolve({redirect: login});
      });
  }

  function wait(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  function runPolling({url, requestOptions} = {}) {
    return request(url, requestOptions).then(async (resp) => {
      if (resp.status === 401) {
        if (isTokenUpdating) {
          // defer request
          await wait(50);
          return runPolling({url, requestOptions});
        }
        // start to update access token
        isTokenUpdating = true;
        return updateRefreshToken({url, requestOptions});
      }
      return handleResponse(resp);
    });
  }

  return (url, requestOptions = {}) => runPolling({url, requestOptions});
}
